import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { 
    MessageSquare, Send, ThumbsUp, Heart, PartyPopper, 
    Laugh, MessageCircle, RefreshCw,
    Sparkles, Plus, Clock, Paperclip, FileText, Download, X, Image as ImageIcon, File as FileIcon, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Linkify } from '@/lib/linkify';

const wallsAPI = {
    getAvailableWalls: () => api.get('/walls/available'),
    getPosts: (wallType, params) => api.get(`/walls/${wallType}/posts`, { params }),
    createPost: (data) => api.post('/walls/posts', data),
    addReaction: (postId, data) => api.post(`/walls/posts/${postId}/reactions`, data),
    addComment: (postId, data) => api.post(`/walls/posts/${postId}/comments`, data),
    uploadAttachment: (postId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/walls/posts/${postId}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

const REACTION_EMOJIS = [
    { emoji: '👍', label: "J'aime" },
    { emoji: '❤️', label: "J'adore" },
    { emoji: '🎉', label: 'Bravo' },
    { emoji: '😂', label: 'Haha' },
];

const FILE_ICONS = {
    pdf: { color: 'text-red-500 bg-red-50', label: 'PDF' },
    doc: { color: 'text-blue-600 bg-blue-50', label: 'DOC' },
    docx: { color: 'text-blue-600 bg-blue-50', label: 'DOCX' },
    xls: { color: 'text-green-600 bg-green-50', label: 'XLS' },
    xlsx: { color: 'text-green-600 bg-green-50', label: 'XLSX' },
    ppt: { color: 'text-orange-500 bg-orange-50', label: 'PPT' },
    pptx: { color: 'text-orange-500 bg-orange-50', label: 'PPTX' },
    txt: { color: 'text-slate-500 bg-slate-50', label: 'TXT' },
    csv: { color: 'text-teal-500 bg-teal-50', label: 'CSV' },
};

function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function FileAttachment({ att, postId }) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    
    const handleDownload = async () => {
        try {
            const response = await api.get(
                `/walls/posts/${postId}/attachments/${att.id}/download`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', att.original_filename || 'fichier');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Erreur lors du téléchargement');
        }
    };

    const fileInfo = FILE_ICONS[att.file_type] || { color: 'text-slate-500 bg-slate-50', label: att.file_type?.toUpperCase() || '?' };

    return (
        <div 
            data-testid={`file-attachment-${att.id}`}
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer group"
            onClick={handleDownload}
        >
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xs", fileInfo.color)}>
                {fileInfo.label}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{att.original_filename}</p>
                <p className="text-xs text-slate-500">{formatFileSize(att.file_size)}</p>
            </div>
            <Download className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors shrink-0" />
        </div>
    );
}

// Composant Post individuel
function FeedPost({ post, onReact, onComment, currentUserId }) {
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [showReactions, setShowReactions] = useState(false);

    // Adapter le format des réactions (ancien format = objet {emoji: [users]}, nouveau = array)
    const reactionsArray = Array.isArray(post.reactions) ? post.reactions : [];
    const reactionsObj = !Array.isArray(post.reactions) ? (post.reactions || {}) : {};
    
    let userReaction = null;
    let totalReactions = 0;
    
    if (Array.isArray(post.reactions)) {
        userReaction = post.reactions.find(r => r.user_id === currentUserId);
        totalReactions = post.reactions.length;
    } else {
        // Format objet: {emoji: [{user_id, user_name}]}
        for (const [emoji, users] of Object.entries(reactionsObj)) {
            totalReactions += (users || []).length;
            if ((users || []).some(u => u.user_id === currentUserId)) {
                userReaction = { emoji };
            }
        }
    }
    
    const totalComments = post.comments?.length || 0;

    const imageAttachments = (post.attachments || []).filter(att => 
        att.is_image || att.image_data_url || att.stored_filename
    );
    const fileAttachments = (post.attachments || []).filter(att => 
        !att.is_image && !att.image_data_url && (att.has_storage || att.storage_path || att.file_type)
    );

    const handleReaction = (emoji) => {
        onReact(post.id, emoji);
        setShowReactions(false);
    };

    const handleComment = () => {
        if (newComment.trim()) {
            onComment(post.id, newComment);
            setNewComment('');
        }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`feed-post-${post.id}`}>
            {/* Header */}
            <div className="p-4 flex items-start gap-3">
                <Avatar className="w-10 h-10 ring-2 ring-yellow-400/30">
                    {post.author_avatar ? (
                        <AvatarImage src={post.author_avatar} />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white font-semibold text-sm">
                        {post.author_name?.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{post.author_name}</span>
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                            {post.author_role === 'admin_full' ? 'Admin' : 
                             post.author_role === 'vp' ? 'VP' : 'Apporteur'}
                        </Badge>
                        {post.wall_type === 'direction' && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Direction</Badge>
                        )}
                        {post.wall_type === 'team' && post.team_name && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">{post.team_name}</Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: fr })}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-4 pb-3">
                <p className="text-slate-800 whitespace-pre-wrap leading-relaxed"><Linkify text={post.content} /></p>
            </div>

            {/* Images */}
            {imageAttachments.length > 0 && (
                <div className="px-4 pb-3 space-y-2">
                    {imageAttachments.map((att, idx) => {
                        const imgSrc = att.image_data_url || (att.stored_filename ? `/uploads/${att.stored_filename}` : null);
                        if (!imgSrc) return null;
                        return (
                            <img 
                                key={att.id || idx}
                                src={imgSrc} 
                                alt={att.original_filename || ''} 
                                className="rounded-lg max-h-80 w-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                                onClick={() => window.open(imgSrc, '_blank')}
                            />
                        );
                    })}
                </div>
            )}

            {/* Fichiers téléchargeables */}
            {fileAttachments.length > 0 && (
                <div className="px-4 pb-3 space-y-2">
                    {fileAttachments.map((att) => (
                        <FileAttachment key={att.id} att={att} postId={post.id} />
                    ))}
                </div>
            )}

            {/* Stats */}
            {(totalReactions > 0 || totalComments > 0) && (
                <div className="px-4 py-2 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
                    <div className="flex items-center gap-1">
                        {totalReactions > 0 && (
                            <>
                                <span className="flex -space-x-1">
                                    {Array.isArray(post.reactions) ? (
                                        REACTION_EMOJIS.slice(0, 3).map(r => (
                                            post.reactions?.some(pr => pr.emoji === r.emoji) && (
                                                <span key={r.emoji} className="text-sm">{r.emoji}</span>
                                            )
                                        ))
                                    ) : (
                                        Object.keys(reactionsObj).slice(0, 3).map(emoji => (
                                            <span key={emoji} className="text-sm">{emoji}</span>
                                        ))
                                    )}
                                </span>
                                <span>{totalReactions}</span>
                            </>
                        )}
                    </div>
                    {totalComments > 0 && (
                        <button 
                            onClick={() => setShowComments(!showComments)}
                            className="hover:underline"
                        >
                            {totalComments} commentaire{totalComments > 1 ? 's' : ''}
                        </button>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="px-2 py-1 flex items-center border-t border-slate-100">
                <div className="relative flex-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                            "w-full justify-center gap-2 text-slate-600 hover:text-yellow-600 hover:bg-yellow-50",
                            userReaction && "text-yellow-600"
                        )}
                        onClick={() => setShowReactions(!showReactions)}
                        data-testid={`react-btn-${post.id}`}
                    >
                        {userReaction ? (
                            <span className="text-lg">{userReaction.emoji}</span>
                        ) : (
                            <ThumbsUp className="w-4 h-4" />
                        )}
                        <span className="text-sm">J'aime</span>
                    </Button>
                    
                    {showReactions && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white rounded-full shadow-lg border px-2 py-1 flex gap-1 z-10">
                            {REACTION_EMOJIS.map(r => (
                                <button
                                    key={r.emoji}
                                    onClick={() => handleReaction(r.emoji)}
                                    className="text-xl hover:scale-125 transition-transform p-1"
                                    title={r.label}
                                >
                                    {r.emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-center gap-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                    onClick={() => setShowComments(!showComments)}
                    data-testid={`comment-btn-${post.id}`}
                >
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm">Commenter</span>
                </Button>
            </div>

            {/* Commentaires */}
            {showComments && (
                <div className="px-4 py-3 bg-slate-50 border-t space-y-3">
                    {post.comments?.map(comment => (
                        <div key={comment.id} className="flex gap-2">
                            <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                                    {comment.author_name?.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 bg-white rounded-xl px-3 py-2 text-sm">
                                <span className="font-medium text-slate-900">{comment.author_name}</span>
                                <p className="text-slate-700"><Linkify text={comment.content} /></p>
                            </div>
                        </div>
                    ))}

                    <div className="flex gap-2">
                        <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-yellow-100 text-yellow-700 text-xs">
                                Moi
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 flex gap-2">
                            <Input
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Écrire un commentaire..."
                                className="flex-1 bg-white rounded-full text-sm h-9"
                                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                                data-testid={`comment-input-${post.id}`}
                            />
                            <Button
                                size="sm"
                                onClick={handleComment}
                                disabled={!newComment.trim()}
                                className="rounded-full bg-yellow-500 hover:bg-yellow-600 h-9 w-9 p-0"
                                data-testid={`comment-submit-${post.id}`}
                            >
                                <Send className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Widget principal
export function WallFeedWidget() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewPost, setShowNewPost] = useState(false);
    const [newPostContent, setNewPostContent] = useState('');
    const [selectedWall, setSelectedWall] = useState('direction');
    const [availableWalls, setAvailableWalls] = useState([]);
    const [posting, setPosting] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadWalls();
        loadPosts();
    }, []);

    const loadWalls = async () => {
        try {
            const res = await wallsAPI.getAvailableWalls();
            setAvailableWalls(res.data || []);
        } catch (err) {
            console.error('Erreur chargement murs:', err);
        }
    };

    const loadPosts = async () => {
        setLoading(true);
        try {
            const walls = ['direction', 'direction_vp', 'team'];
            const allPosts = [];
            
            for (const wall of walls) {
                try {
                    const res = await wallsAPI.getPosts(wall, { limit: 10 });
                    const postsData = res.data?.posts || res.data || [];
                    if (Array.isArray(postsData)) {
                        allPosts.push(...postsData.map(p => ({ ...p, wall_type: p.wall_type || wall })));
                    }
                } catch (e) {
                    // Mur non accessible
                }
            }
            
            allPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setPosts(allPosts.slice(0, 15));
        } catch (err) {
            console.error('Erreur chargement posts:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 10) {
            toast.error('Maximum 10 fichiers par publication');
            return;
        }
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleNewPost = async () => {
        if (!newPostContent.trim() && selectedFiles.length === 0) return;
        
        setPosting(true);
        try {
            // Créer le post
            const res = await wallsAPI.createPost({
                wall_type: selectedWall,
                content: newPostContent || '(fichier joint)'
            });
            const postId = res.data?.post_id;
            
            // Uploader les fichiers
            if (postId && selectedFiles.length > 0) {
                for (const file of selectedFiles) {
                    try {
                        await wallsAPI.uploadAttachment(postId, file);
                    } catch (uploadErr) {
                        console.error('Erreur upload fichier:', uploadErr);
                        toast.error(`Erreur upload: ${file.name}`);
                    }
                }
            }
            
            toast.success('Publication créée !');
            setNewPostContent('');
            setSelectedFiles([]);
            setShowNewPost(false);
            loadPosts();
        } catch (err) {
            toast.error('Erreur lors de la publication');
        } finally {
            setPosting(false);
        }
    };

    const handleReaction = async (postId, emoji) => {
        try {
            await wallsAPI.addReaction(postId, { emoji });
            loadPosts();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    const handleComment = async (postId, content) => {
        try {
            await wallsAPI.addComment(postId, { content });
            loadPosts();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    if (loading) {
        return (
            <Card className="col-span-full">
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="col-span-full overflow-hidden" data-testid="wall-feed-widget">
            <CardHeader className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white pb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur">
                            <Sparkles className="w-6 h-6" />
                        </div>
                        <div>
                            <CardTitle className="text-xl font-bold text-white flex items-center gap-2">
                                Fil d'Actualités
                                <Badge className="bg-white/20 text-white border-0 ml-2">
                                    {posts.length} publications
                                </Badge>
                            </CardTitle>
                            <p className="text-white/80 text-sm mt-0.5">
                                Restez connecté avec votre équipe
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={loadPosts}
                            className="text-white hover:bg-white/20"
                            data-testid="refresh-feed-btn"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                            onClick={() => setShowNewPost(!showNewPost)}
                            className="bg-white text-orange-600 hover:bg-white/90"
                            data-testid="new-post-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Publier
                        </Button>
                    </div>
                </div>

                {showNewPost && (
                    <div className="mt-4 bg-white/10 backdrop-blur rounded-xl p-4 space-y-3">
                        <Textarea
                            value={newPostContent}
                            onChange={(e) => setNewPostContent(e.target.value)}
                            placeholder="Partagez une info, une victoire, une annonce..."
                            className="bg-white/90 text-slate-900 border-0 placeholder:text-slate-400 resize-none"
                            rows={3}
                            data-testid="new-post-content"
                        />
                        
                        {/* Fichiers sélectionnés */}
                        {selectedFiles.length > 0 && (
                            <div className="space-y-2">
                                {selectedFiles.map((file, idx) => {
                                    const isImage = file.type.startsWith('image/');
                                    return (
                                        <div key={idx} className="flex items-center gap-2 bg-white/80 rounded-lg px-3 py-2 text-sm text-slate-800">
                                            {isImage ? (
                                                <ImageIcon className="w-4 h-4 text-blue-500 shrink-0" />
                                            ) : (
                                                <FileIcon className="w-4 h-4 text-red-500 shrink-0" />
                                            )}
                                            <span className="flex-1 truncate">{file.name}</span>
                                            <span className="text-xs text-slate-500 shrink-0">{formatFileSize(file.size)}</span>
                                            <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-500">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <select
                                    value={selectedWall}
                                    onChange={(e) => setSelectedWall(e.target.value)}
                                    className="bg-white/20 text-white text-sm rounded-lg px-3 py-1.5 border-0 outline-none"
                                    data-testid="wall-type-select"
                                >
                                    <option value="direction" className="text-slate-900">Direction (tous)</option>
                                    <option value="direction_vp" className="text-slate-900">Direction + VP</option>
                                    <option value="team" className="text-slate-900">Mon équipe</option>
                                </select>
                                
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                    className="hidden"
                                    onChange={handleFileSelect}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-white hover:bg-white/20"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="attach-file-btn"
                                >
                                    <Paperclip className="w-4 h-4 mr-1" />
                                    Fichier
                                </Button>
                            </div>
                            <Button
                                onClick={handleNewPost}
                                disabled={(!newPostContent.trim() && selectedFiles.length === 0) || posting}
                                className="bg-white text-orange-600 hover:bg-white/90"
                                data-testid="submit-post-btn"
                            >
                                {posting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                        Publication...
                                    </>
                                ) : 'Publier'}
                            </Button>
                        </div>
                    </div>
                )}
            </CardHeader>

            <CardContent className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                {posts.length === 0 ? (
                    <div className="text-center py-12">
                        <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">Aucune publication pour le moment</p>
                        <p className="text-slate-400 text-sm mt-1">Soyez le premier à partager !</p>
                    </div>
                ) : (
                    posts.map(post => (
                        <FeedPost
                            key={post.id}
                            post={post}
                            onReact={handleReaction}
                            onComment={handleComment}
                            currentUserId={user?.id}
                        />
                    ))
                )}
            </CardContent>
        </Card>
    );
}

export default WallFeedWidget;
